import {
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import React from 'react';
import {
  TaxonomyE2EType,
  TaxonomyFunctionItem,
  TaxonomySubProcessType,
} from '../../slices/allTaxonomy/taxonomyTypes';

interface TaxonomyBreadcrumbProps {
  selectedTaxonomy: TaxonomyFunctionItem | null;
  selectedE2E: TaxonomyE2EType | null;
  selectedSubprocess: TaxonomySubProcessType | null;
  selectedCard?: { name: string } | null;
  onResetAll: () => void;
  onResetTaxonomy: () => void;
  onResetE2E: () => void;
  variant?: 'default' | 'diagram';
  onEdit?: () => void;
  titleRight?: React.ReactNode;
  subtitleRight?: React.ReactNode;
}

const TaxonomyBreadcrumb: React.FC<TaxonomyBreadcrumbProps> = ({
  selectedTaxonomy,
  selectedE2E,
  selectedSubprocess,
  selectedCard,
  onResetAll,
  onResetTaxonomy,
  onResetE2E,
  variant = 'default',
  onEdit,
  titleRight,
  subtitleRight,
}) => {
  const getSection = () => {
    if (selectedTaxonomy) {
      const office = selectedTaxonomy.office;
      switch (office) {
        case '1':
          return 'Front Office';
        case '2':
          return 'Middle Office';
        case '3':
          return 'Back Office';
        default:
          return '';
      }
    }
    return '';
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Common breadcrumb text styling
  const breadcrumbTextStyles = {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '1.5',
    letterSpacing: '-0.02em',
    color: '#626265',
    maxWidth: '300px', // Increased to prevent wrapping
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    flexShrink: 0,
    paddingBottom: '2px',
  };

  const getCurrentLevel = () => {
    if (selectedSubprocess) return 'subprocess';
    if (selectedE2E) return 'e2e';
    if (selectedTaxonomy) return 'taxonomy';
    return null;
  };

  const renderBreadcrumbLinks = () => {
    const items = [];
    const currentLevel = getCurrentLevel();

    // Add section link if any selection exists
    if (selectedTaxonomy || selectedE2E || selectedSubprocess) {
      items.push(
        <Link
          key="section"
          underline="hover"
          color="inherit"
          onClick={onResetAll}
          sx={{
            cursor: 'pointer',
            ...breadcrumbTextStyles,
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          {getSection()}
        </Link>
      );
    }

    // Add taxonomy link if it exists and we're at a deeper level
    if (selectedTaxonomy && (selectedE2E || selectedSubprocess)) {
      items.push(
        <Link
          key="taxonomy"
          underline="hover"
          color="inherit"
          onClick={onResetTaxonomy}
          sx={{
            cursor: 'pointer',
            ...breadcrumbTextStyles,
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          {selectedTaxonomy.name}
        </Link>
      );
    }

    // Add E2E link if subprocess is selected
    if (selectedE2E && selectedSubprocess) {
      items.push(
        <Link
          key="e2e"
          underline="hover"
          color="inherit"
          onClick={onResetE2E}
          sx={{
            cursor: 'pointer',
            ...breadcrumbTextStyles,
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          {selectedE2E.name}
        </Link>
      );
    }

    // Add current level as Typography
    const currentItem = selectedSubprocess?.name || selectedE2E?.name || selectedTaxonomy?.name;
    if (currentItem && currentLevel) {
      items.push(
        <Typography key="current" sx={breadcrumbTextStyles}>
          {currentItem}
        </Typography>
      );
    }

    return items;
  };

  const getMobilePathText = () => {
    const parts = [];
    if (selectedTaxonomy) {
      parts.push(selectedTaxonomy.name);
    }
    // if (selectedE2E) {
    //   parts.push(selectedE2E.name);
    // }
    if (selectedSubprocess) {
      parts.push(selectedSubprocess.name);
    }
    if (selectedCard) {
      parts.push(selectedCard.name);
    }
    return parts.join(' / ');
  };

  const getMobilePathTitle = () => {
    const parts = [];
    if (selectedTaxonomy) {
      parts.push(selectedTaxonomy.name);
    }
    // if (selectedE2E) {
    //   parts.push(selectedE2E.name);
    // }
    if (selectedSubprocess) {
      parts.push(selectedSubprocess.name);
    }
    if (selectedCard) {
      parts.push(selectedCard.name);
    }
    return parts.join(' / ');
  };

  // Update the render logic in the diagram variant
  if (variant === 'diagram') {
    const currentLevel = getCurrentLevel();

    const getCurrentHeading = () => {
      // Prioritize selected card name if available
      if (selectedCard?.name) {
        return selectedCard.name;
      }
      if (currentLevel === 'subprocess' && selectedSubprocess) {
        return selectedSubprocess.name;
      }
      if (currentLevel === 'e2e' && selectedE2E) {
        return selectedE2E.name;
      }
      if (currentLevel === 'taxonomy' && selectedTaxonomy) {
        return selectedTaxonomy.name;
      }
      return getSection();
    };

    return (
      <Stack spacing={1} sx={{ p: { xs: 1, md: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,

            ...(isMobile ? { justifyContent: 'space-between', alignItems: 'center' } : {}),
          }}
        >
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '18px', md: '24px' },
              fontFamily: 'Poppins, sans-serif',
              letterSpacing: '0',
              color: '#313131',
              lineHeight: { xs: 1.2, md: '100%' },
              maxWidth: '100%',
              // overflow: 'hidden',
              display: { xs: '-webkit-box', md: 'block' },
              WebkitLineClamp: { xs: 2, md: 1 },
              WebkitBoxOrient: { xs: 'vertical', md: 'unset' },
              textOverflow: { xs: 'ellipsis', md: 'unset' },
              whiteSpace: { xs: 'normal', md: 'nowrap' },
            }}
          >
            {getCurrentHeading()}
          </Typography>
          {!isMobile && (
            <IconButton
              sx={{
                color: '#666',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
              onClick={onEdit}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {isMobile && titleRight}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0.5, md: 1 },
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: { xs: '12px', md: '16px' },
            fontWeight: 500,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            py: { xs: 0.5, md: 1 },
            ...(isMobile ? { justifyContent: 'space-between' } : {}),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.25, md: 0.5 },
              minWidth: 0,
              overflow: 'hidden',
              flex: 1,
            }}
          >
            {isMobile ? (
              // Single line mobile breadcrumb with concatenated text and ellipsis
              <Typography
                sx={{
                  ...breadcrumbTextStyles,
                  fontSize: '12px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
                title={getMobilePathTitle()}
              >
                {getMobilePathText()}
              </Typography>
            ) : (
              <>
                {selectedTaxonomy && currentLevel !== 'taxonomy' && (
                  <>
                    <Typography sx={breadcrumbTextStyles} title={selectedTaxonomy.name}>
                      {selectedTaxonomy.name}
                    </Typography>
                    {(currentLevel === 'subprocess' || selectedCard) && (
                      <Typography sx={breadcrumbTextStyles}>{' / '}</Typography>
                    )}
                  </>
                )}

                {selectedE2E && (currentLevel === 'subprocess' || selectedCard) && (
                  <>
                    <Typography sx={breadcrumbTextStyles} title={selectedE2E.name}>
                      {selectedE2E.name}
                    </Typography>
                    <Typography sx={breadcrumbTextStyles}>{' / '}</Typography>
                  </>
                )}

                {currentLevel === 'subprocess' && (
                  <>
                    <Typography sx={breadcrumbTextStyles} title={selectedSubprocess?.name}>
                      {selectedSubprocess?.name}
                    </Typography>
                    {selectedCard && (
                      <>
                        <Typography sx={breadcrumbTextStyles}>{' / '}</Typography>
                        <Typography sx={breadcrumbTextStyles} title={selectedCard.name}>
                          {selectedCard.name}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pr: 0 }}>
              {subtitleRight}
            </Box>
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        m: 1,
        px: 1,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            ...breadcrumbTextStyles,
            mx: 1,
          },
          '& .MuiBreadcrumbs-ol': {
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            //   alignItems: 'center',
            // },
            // '& .MuiBreadcrumbs-li': {
            //   display: 'flex',
            //   alignItems: 'center',
          },
        }}
        separator="/"
      >
        {renderBreadcrumbLinks()}
      </Breadcrumbs>
    </Box>
  );
};

export default TaxonomyBreadcrumb;
